import { useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";

/**
 * Detección de plataforma vía User-Agent + heurística táctil.
 * - iOS: iPhone / iPad / iPod (excluye Windows).
 * - Android: cualquier UA que contenga "Android".
 * - Fallback: dispositivos táctiles sin UA reconocible → iOS (estilo flotante).
 */
export function detectPlatform(): "ios" | "android" {
  if (typeof navigator === "undefined") return "ios";
  const ua = navigator.userAgent || "";
  if (/iPad|iPhone|iPod/.test(ua) && !/Windows/.test(ua)) return "ios";
  if (/Android/i.test(ua)) return "android";
  if (navigator.maxTouchPoints > 1) return "ios";
  return "ios";
}

type Variant = "auto" | "ios" | "android";

interface NavItem {
  key: string;
  label: string;
  icon: string;
  activeIcon: string;
  to: string;
  publish?: boolean;
}

interface MobileNavbarProps {
  /** Fuerza una variante para simular (por defecto "auto" detecta el SO). */
  variant?: Variant;
}

export default function MobileNavbar({ variant = "auto" }: MobileNavbarProps) {
  const { user, role, accountType, sellerStatus } = useAuth();
  const location = useLocation();

  const panelPath =
    role === "super_admin"
      ? "/super-admin"
      : role === "admin"
        ? "/admin/dashboard"
        : accountType === "seller"
          ? "/seller/dashboard"
          : null;

  // "Publicar" abre el formulario de publicación (mismas opciones que un
  // vendedor). Admin y Super Admin también pueden publicar directamente.
  const canPublish =
    sellerStatus === "approved" ||
    accountType === "seller" ||
    role === "admin" ||
    role === "super_admin";
  const publishPath = canPublish ? "/seller/dashboard" : "/registro";
  const accountPath = user ? panelPath ?? "/" : "/login";

  // El botón "Publicar" se oculta mientras la solicitud de vendedor
  // está en revisión o la cuenta está suspendida.
  const showPublish =
    sellerStatus !== "pending" && sellerStatus !== "suspended";

  const items: NavItem[] = [
    { key: "home", label: "Inicio", icon: "ri-home-5-line", activeIcon: "ri-home-5-fill", to: "/" },
    { key: "explore", label: "Explorar", icon: "ri-compass-3-line", activeIcon: "ri-compass-3-fill", to: "/#categorias" },
    ...(showPublish
      ? [{ key: "publish", label: "Publicar", icon: "ri-add-line", activeIcon: "ri-add-fill", to: publishPath, publish: true } as NavItem]
      : []),
    { key: "featured", label: "Destacados", icon: "ri-fire-line", activeIcon: "ri-fire-fill", to: "/#destacados" },
    { key: "account", label: user ? "Mi Cuenta" : "Entrar", icon: "ri-user-line", activeIcon: "ri-user-fill", to: accountPath },
  ];

  const isActive = (to: string): boolean => {
    if (to === "/") return location.pathname === "/" && !location.hash;
    if (to.startsWith("/#")) {
      return location.pathname === "/" && location.hash === to.slice(1);
    }
    return location.pathname === to;
  };

  const platform = useMemo<"ios" | "android">(
    () => (variant === "auto" ? detectPlatform() : variant),
    [variant],
  );

  const isIOS = platform === "ios";

  return (
    <div data-platform={platform} className="lg:hidden">
      {isIOS ? (
        /* ─────────────────────────────────────────────
           iOS · Barra flotante "Liquid Glass"
        ───────────────────────────────────────────── */
        <div
          className="pointer-events-none fixed inset-x-0 bottom-0 z-40 flex justify-center px-4"
          style={{ paddingBottom: "calc(0.875rem + env(safe-area-inset-bottom))" }}
        >
          <nav className="glass-sheen glass-strong pointer-events-auto flex items-center gap-0.5 rounded-full p-1.5 shadow-lift">
            {items.map((item) =>
              item.publish ? (
                <Link
                  key={item.key}
                  to={item.to}
                  aria-label={item.label}
                  className="flex h-12 w-12 cursor-pointer items-center justify-center rounded-full bg-primary-500 text-white shadow-lift transition-transform hover:scale-105 active:scale-95"
                >
                  <i className="ri-add-line text-2xl"></i>
                </Link>
              ) : (
                <Link
                  key={item.key}
                  to={item.to}
                  className={`flex cursor-pointer flex-col items-center justify-center gap-0.5 rounded-full px-3 py-1.5 text-[10px] font-medium transition-colors ${
                    isActive(item.to)
                      ? "text-primary-600"
                      : "text-foreground-500 hover:text-foreground-800"
                  }`}
                >
                  <i
                    className={`${
                      isActive(item.to) ? item.activeIcon : item.icon
                    } text-xl`}
                  ></i>
                  <span className="leading-none">{item.label}</span>
                </Link>
              ),
            )}
          </nav>
        </div>
      ) : (
        /* ─────────────────────────────────────────────
           Android · Barra fija de ancho completo (Material You)
        ───────────────────────────────────────────── */
        <nav
          className="fixed inset-x-0 bottom-0 z-40 border-t border-background-200/70 bg-background-50/95 backdrop-blur-xl"
          style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        >
          <div className="flex items-center justify-around gap-1 px-2 py-1.5">
            {items.map((item) =>
              item.publish ? (
                <Link
                  key={item.key}
                  to={item.to}
                  aria-label={item.label}
                  className="flex h-11 w-11 cursor-pointer items-center justify-center rounded-full bg-primary-500 text-white transition-transform hover:scale-105 active:scale-95"
                >
                  <i className="ri-add-line text-2xl"></i>
                </Link>
              ) : (
                <Link
                  key={item.key}
                  to={item.to}
                  className={`flex cursor-pointer flex-col items-center justify-center gap-0.5 rounded-full px-3 py-1.5 text-[10px] font-medium transition-colors ${
                    isActive(item.to)
                      ? "bg-primary-100 text-primary-700"
                      : "text-foreground-500 hover:bg-background-100"
                  }`}
                >
                  <i
                    className={`${
                      isActive(item.to) ? item.activeIcon : item.icon
                    } text-xl`}
                  ></i>
                  <span className="leading-none">{item.label}</span>
                </Link>
              ),
            )}
          </div>
        </nav>
      )}
    </div>
  );
}